import React, { useCallback, useContext, useState } from 'react';
import {
  Col,
  ColorPicker,
  Divider,
  Drawer,
  Form,
  Row,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  VertButton,
  VertContainer,
  VertSelect,
  VertTokens,
} from '@vertem-core/vertice-ds';
import ModalResultSkin from './ModalResultSkin';
import ModalUploadJSON from './ModalUploadJSON';
import { useSelector } from 'react-redux';
import { ShareAltOutlined } from '@ant-design/icons';
import { VertInput } from '@vertem-core/vertice-ds';
import { FaX } from 'react-icons/fa6';

const DrawerStyle = ({
  open = false,
  setOpen,
  initialTokens,
  setInitialTokensValuesSkin,
}) => {
  const [form] = Form.useForm();
  const userProfile = useSelector((state) => state.oidc.user.profile);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModalUpload, setIsOpenModalUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaignInfos, setCampaignInfos] = useState({});
  const { designTokens } = useContext(VertTokens);

  const onJSONUpload = useCallback((json) => {
    form.setFieldsValue(json);

    setInitialTokensValuesSkin(json);

    localStorage.setItem('designTokens', JSON.stringify(designTokens));

    setOpen(false);

    setLoading(false);
  }, []);

  const onClose = () => {
    setOpen(false);
  };

  const toHexColor = (colorValue) => {
    if (typeof colorValue === 'string' && colorValue.startsWith('#')) {
      return colorValue;
    } else if (colorValue && typeof colorValue.toHexString === 'function') {
      return colorValue.toHexString();
    }
    return '#FFFFFF';
  };

  const convertHexToRgba = (hex, opacity) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const convertRgbaToHex = (rgba) => {
    const result = rgba.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(0|1|0\.\d+))?\)$/
    );
    if (!result) {
      return null;
    }

    let r = parseInt(result[1], 10).toString(16),
      g = parseInt(result[2], 10).toString(16),
      b = parseInt(result[3], 10).toString(16),
      a = Math.round(parseFloat(result[4] || 1) * 255).toString(16);

    if (r.length === 1) r = '0' + r;
    if (g.length === 1) g = '0' + g;
    if (b.length === 1) b = '0' + b;
    if (a.length === 1) a = '0' + a;

    return `#${r}${g}${b}${a}`;
  };

  const handleFormSubmit = (values) => {
    let headerColorBackground = convertHexToRgba(
      toHexColor(values['header-color-background']),
      0.6
    );
    let convertedHeaderColorBackground = convertRgbaToHex(
      headerColorBackground
    );
    const data = {
      favicon: initialTokens?.favicon,
      'color-detached': toHexColor(values['color-detached']),
      'header-color-menu-dropdown': toHexColor(
        values['header-color-menu-dropdown']
      ),
      'color-button-text': toHexColor(values['color-button-text']),
      'color-detached-lighter': toHexColor(values['color-detached-lighter']),

      'color-background': convertedHeaderColorBackground,
      'color-detached-super-lighter': convertedHeaderColorBackground,
      'color-highlight-super-lighter': convertedHeaderColorBackground,
      'color-highlight': toHexColor(values['color-highlight']),
      'color-highlight-lighter': toHexColor(values['color-highlight-lighter']),
      'color-highlight-darker': toHexColor(values['color-highlight-darker']),

      'header-color-background': toHexColor(values['header-color-background']),
      'header-color-menu-text': toHexColor(values['header-color-menu-text']),
      'header-color-menu-text-hover': toHexColor(
        values['header-color-menu-text-hover']
      ),

      'header-color-menu-text-active': toHexColor(
        values['header-color-menu-text-active']
      ),
      'header-color-border-search-button': toHexColor(
        values['header-color-border-search-button']
      ),
      'header-color-search-icon': toHexColor(
        values['header-color-search-icon']
      ),
      'header-color-points-content': toHexColor(
        values['header-color-points-content']
      ),
      'header-color-search-button': toHexColor(
        values['header-color-search-button']
      ),
      'header-color-search-button-hover': toHexColor(
        values['header-color-search-button-hover']
      ),
      'header-color-close-button-responsive': toHexColor(
        values['header-color-close-button-responsive']
      ),
      'header-color-icon-participant-info': toHexColor(
        values['header-color-icon-participant-info']
      ),
      'footer-color-background': toHexColor(values['footer-color-background']),
      'footer-size-logo-maxwidth': values['footer-size-logo-maxwidth'],
      'footer-size-logo-maxheight': values['footer-size-logo-maxheight'],
      'footer-size-logo-maxwidth-mobile-md':
        values['footer-size-logo-maxwidth-mobile-md'],
      'footer-size-logo-maxwidth-mobile-sm':
        values['footer-size-logo-maxwidth-mobile-sm'],
      'header-color-points-border': toHexColor(
        values['header-color-points-border']
      ),
      'header-color-menu-text-points': toHexColor(
        values['header-color-menu-text-points']
      ),
      logotipo: values.logotipo,
      'logotipo-footer': values['logotipo-footer'],
      'footer-color-button-text': toHexColor(
        values['footer-color-button-text']
      ),
      'footer-logo-object-fit': values['footer-logo-object-fit'],
      'footer-logo-box-sizing': values['footer-logo-box-sizing'],
      'header-color-icon-dollar-responsive': toHexColor(
        values['header-color-icon-dollar-responsive']
      ),
      'header-color-text-balance-responsive': toHexColor(
        values['header-color-text-balance-responsive']
      ),
    };

    setInitialTokensValuesSkin((prev) => {
      return {
        ...prev,
        ...data,
      };
    });
    setCampaignInfos({
      campaignId: values.campaignId,
      catalogId: values.catalogId,
    });
  };

  return (
    <>
      <Drawer
        title=""
        placement={'right'}
        width={500}
        onClose={onClose}
        open={open}
        closeIcon={false}
      >
        <VertContainer spacingX="small" spacingY="small">
          <Row gutter={[16, 16]}>
            <Col
              span={12}
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Typography.Title level={3}>Customizar Skin</Typography.Title>
            </Col>
            <Col
              span={12}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <VertButton
                onClick={onClose}
                block
                type="default"
                icon={<FaX />}
              />
            </Col>
          </Row>
          <Divider />

          <Row
            gutter={[16, 16]}
            style={{
              marginBottom: 16,
            }}
          >
            <Col span={24}>
              <Typography.Text strong>
                Carregue um JSON para preencher os campos automaticamente
              </Typography.Text>
            </Col>

            <Col span={24}>
              <VertButton
                onClick={() => setIsOpenModalUpload(true)}
                block
                type="default"
              >
                Carregar JSON
              </VertButton>
            </Col>
          </Row>
          <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={{
                    campaignId: '' || userProfile?.['custom:campaign_id'],
                    catalogId: '' || userProfile?.['custom:catalog_id'],
                    favicon: initialTokens?.favicon,

                    //  CORES DE BOTÕES, INPUTS E ETC
                    'color-button-text': initialTokens?.['color-button-text'],
                    'color-detached': initialTokens?.['color-detached'],
                    'color-detached-lighter':
                      initialTokens?.['color-detached-lighter'],
                    'color-background': initialTokens?.['color-background'],
                    'color-highlight': initialTokens?.['color-highlight'],
                    'color-highlight-lighter':
                      initialTokens?.['color-highlight-lighter'],
                    'color-highlight-darker':
                      initialTokens?.['color-highlight-darker'],
                    'header-color-menu-dropdown':
                      initialTokens?.['header-color-menu-dropdown'],
                    'header-color-border-search-button':
                      initialTokens?.['header-color-border-search-button'],

                    // CORES DE HEADER
                    'header-color-background':
                      initialTokens?.['header-color-background'],
                    'header-color-menu-text':
                      initialTokens?.['header-color-menu-text'],
                    'header-color-icon-participant-info':
                      initialTokens?.['header-color-icon-participant-info'],

                    'header-color-search-icon':
                      initialTokens?.['header-color-search-icon'],
                    'header-color-points-content':
                      initialTokens?.['header-color-points-content'],
                    'header-color-search-button':
                      initialTokens?.['header-color-search-button'],
                    'header-color-search-button-hover':
                      initialTokens?.['header-color-search-button-hover'],
                    'header-color-close-button-responsive':
                      initialTokens?.['header-color-close-button-responsive'],
                    'footer-color-background':
                      initialTokens?.['footer-color-background'],
                    'footer-size-logo-maxwidth':
                      initialTokens?.['footer-size-logo-maxwidth'],
                    'footer-size-logo-maxheight':
                      initialTokens?.['footer-size-logo-maxheight'],
                    'footer-size-logo-maxwidth-mobile-md':
                      initialTokens?.['footer-size-logo-maxwidth-mobile-md'],
                    'footer-size-logo-maxwidth-mobile-sm':
                      initialTokens?.['footer-size-logo-maxwidth-mobile-sm'],
                    'color-detached-super-lighter':
                      initialTokens?.['color-detached-super-lighter'],
                    'color-highlight-super-lighter':
                      initialTokens?.['color-highlight-super-lighter'],
                    'header-color-points-border':
                      initialTokens?.['header-color-points-border'],
                    'header-color-menu-text-points':
                      initialTokens?.['header-color-menu-text-points'],
                    logotipo: initialTokens?.logotipo,
                    'logotipo-footer': initialTokens['logotipo-footer'],
                    'footer-color-button-text':
                      initialTokens['footer-color-button-text'],
                    'footer-logo-object-fit':
                      initialTokens['footer-logo-object-fit'],
                    'footer-logo-box-sizing':
                      initialTokens['footer-logo-box-sizing'],

                    'header-color-icon-dollar-responsive':
                      initialTokens['header-color-icon-dollar-responsive'],
                    'header-color-text-balance-responsive':
                      initialTokens['header-color-text-balance-responsive'],
                  }}
                  onFinish={handleFormSubmit}
                >
                  <Typography.Title level={4}>
                    Informações da Campanha Desejada
                  </Typography.Title>
                  <Divider />
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        label="Campanha"
                        name="campaignId"
                        rules={[
                          {
                            required: true,
                            message: 'Campo obrigatório!',
                          },
                          {
                            pattern: /^[0-9]*$/,
                            message: 'Somente números!',
                          },
                        ]}
                      >
                        <VertInput />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label="Catálogo de Campanha"
                        name="catalogId"
                        rules={[
                          {
                            required: true,
                            message: 'Campo obrigatório!',
                          },
                          {
                            pattern: /^[0-9]*$/,
                            message: 'Somente números!',
                          },
                        ]}
                      >
                        <VertInput />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Typography.Title level={4}>
                    Componentes Globais
                  </Typography.Title>
                  <Divider />

                  <Form.Item
                    label="FavIcon"
                    name="favicon"
                    extra={
                      <span>
                        FavIcon é o ícone que aparece na aba do navegador. Para
                        redimensionar
                        <a
                          href="https://www.favicon-generator.org/"
                          target="_blank"
                        >
                          {' '}
                          clique aqui.
                        </a>
                      </span>
                    }
                    required
                    rules={[
                      {
                        type: 'url',
                        message: 'URL inválida!',
                      },
                    ]}
                  >
                    <VertInput
                      suffix={
                        <Tooltip title="Abrir link da imagem">
                          <ShareAltOutlined
                            target="_blank"
                            onClick={() => {
                              window.open(initialTokens?.logotipo, '_blank');
                            }}
                            style={{ color: 'rgb(35, 19, 253)' }}
                          />
                        </Tooltip>
                      }
                    />
                  </Form.Item>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item label="BOTÕES" name="color-detached">
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Hover de Botões"
                        name="color-detached-lighter"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Texto de Botões"
                        name="color-button-text"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item label="CARDS, ICONES" name="color-highlight">
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Hover de CARDS, MODAIS(lighter)"
                        name="color-highlight-lighter"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Hover de CARDS, MODAIS(darker)"
                        name="color-highlight-darker"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Typography.Title level={4}>Header</Typography.Title>
                  <Divider />
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        label="Imagem de Logo Header"
                        name="logotipo"
                        required
                        rules={[
                          {
                            type: 'url',
                            message: 'URL inválida!',
                          },
                        ]}
                        extra={
                          <span>
                            Imagem é o ícone que aparece na aba do navegador.
                            Para redimensionar
                            <a
                              href="https://grupoltm.visualstudio.com/Premiacao/_wiki/wikis/Premiacao.wiki/9213/Boas-Pr%C3%A1ticas#redimensionar-imagens"
                              target="_blank"
                            >
                              {' '}
                              clique aqui.
                            </a>
                          </span>
                        }
                      >
                        <VertInput
                          suffix={
                            <Tooltip title="Abrir link da imagem">
                              <ShareAltOutlined
                                target="_blank"
                                onClick={() => {
                                  window.open(
                                    initialTokens?.logotipo,
                                    '_blank'
                                  );
                                }}
                                style={{ color: 'rgb(35, 19, 253)' }}
                              />
                            </Tooltip>
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        label="Cor de Fundo"
                        name="header-color-background"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Texto de Menus do Cabeçalho"
                        name="header-color-menu-text"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Cor do Dropdown do Menu"
                        name="header-color-menu-dropdown"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Icone do Participante"
                        name="header-color-icon-participant-info"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Campo de Busca Icone"
                        name="header-color-search-icon"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cor da Borda do Botão de Pesquisa"
                        name="header-color-border-search-button"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Borda Conteúdo de Pontos"
                        name="header-color-points-border"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cor da Tipografia de Pontos"
                        name="header-color-menu-text-points"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Ícone de Dollar (Responsivo)"
                        name="header-color-icon-dollar-responsive"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Texto de Saldo (Responsivo)"
                        name="header-color-text-balance-responsive"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Ícone de Pesquisa"
                        name="header-color-search-icon"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Conteúdo de Pontos"
                        name="header-color-points-content"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Botão de Pesquisa"
                        name="header-color-search-button"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Botão de Pesquisa (Ao Passar o Mouse)"
                        name="header-color-search-button-hover"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Typography.Title level={4}>Footer</Typography.Title>
                  <Divider />

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        label="Imagem de Logo Footer"
                        name="logotipo-footer"
                        rules={[
                          {
                            type: 'url',
                            message: 'URL inválida!',
                          },
                        ]}
                        extra={
                          <span>
                            Imagem é o ícone que aparece na aba do navegador.
                            Para redimensionar
                            <a
                              href="https://grupoltm.visualstudio.com/Premiacao/_wiki/wikis/Premiacao.wiki/9213/Boas-Pr%C3%A1ticas#redimensionar-imagens"
                              target="_blank"
                            >
                              {' '}
                              clique aqui.
                            </a>
                          </span>
                        }
                        required
                      >
                        <VertInput
                          suffix={
                            <Tooltip title="Abrir link da imagem">
                              <ShareAltOutlined
                                target="_blank"
                                onClick={() => {
                                  window.open(
                                    initialTokens['logotipo-footer'],
                                    '_blank'
                                  );
                                }}
                                style={{ color: 'rgb(35, 19, 253)' }}
                              />
                            </Tooltip>
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Objeto de Ajuste do Logotipo"
                        name="footer-logo-object-fit"
                      >
                        <VertSelect
                          options={[
                            { label: 'Contain', value: 'contain' },
                            { label: 'Cover', value: 'cover' },
                            { label: 'Fill', value: 'fill' },
                            { label: 'Stretch', value: 'stretch' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Box Sizing do Logotipo"
                        name="footer-logo-box-sizing"
                      >
                        <VertSelect
                          options={[
                            { label: 'Border Box', value: 'border-box' },
                            { label: 'Content Box', value: 'content-box' },
                            { label: 'Initial', value: 'initial' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        label="Cor de Fundo do Rodapé"
                        name="footer-color-background"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        label="Cor de Texto de Rodapé"
                        name="footer-color-button-text"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Cor do Botão de Fechar (Responsivo)"
                        name="header-color-close-button-responsive"
                      >
                        <ColorPicker format="hex" size="small" showText />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Tamanho Máximo da Largura do Logotipo (Desktop)"
                        name="footer-size-logo-maxwidth"
                      >
                        <VertInput />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Tamanho Máximo da Altura do Logotipo (Desktop)"
                        name="footer-size-logo-maxheight"
                      >
                        <VertInput />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        label="Tamanho Máximo da Largura do Logotipo (Mobile MD)"
                        name="footer-size-logo-maxwidth-mobile-md"
                      >
                        <VertInput />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Tamanho Máximo da Largura do Logotipo (Mobile SM)"
                        name="footer-size-logo-maxwidth-mobile-sm"
                      >
                        <VertInput />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <VertButton htmlType="submit" block type="highlight">
                        Experimentar Skin
                      </VertButton>

                      <VertButton
                        onClick={() => setIsOpenModal(true)}
                        block
                        htmlType="submit"
                        type="default"
                        style={{ marginTop: 8 }}
                      >
                        Visualizar JSON
                      </VertButton>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Spin>
          <ModalResultSkin
            isOpen={isOpenModal}
            setIsOpenModal={setIsOpenModal}
            data={initialTokens}
            campaignInfos={campaignInfos}
          />
          <ModalUploadJSON
            isOpen={isOpenModalUpload}
            setIsOpenModal={setIsOpenModalUpload}
            setCampaignInfos={setInitialTokensValuesSkin}
            onUpload={onJSONUpload}
            setLoading={setLoading}
          />
        </VertContainer>
      </Drawer>
    </>
  );
};

export default DrawerStyle;
